import React from 'react';

import { Add as AddIcon, Info as InfoIcon } from '@mui/icons-material';
import { Box, Chip, Paper, Typography } from '@mui/material';

import { DateFormatPatterns, StatusLabel } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import {
  PermissiblePurposeStatus,
  PermissiblePurposesItemFragment,
  usePermissiblePurposesListQuery,
  VerifierCredentialStatus,
} from 'src/api';
import { Button, Tooltip } from 'src/components/common';
import type { TableColumn } from 'src/components/table';
import { Table, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { usePaginationQueryVariables, useScreenInfo } from 'src/hooks/common';
import { useCrediblePermissiblePurposeReferenceOptions } from 'src/hooks/permissiblePurposeReference';
import { useModal } from 'src/hooks/shared';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { SxProp } from 'src/types/mui';

export const PermissiblePurposes: React.FC = () => {
  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();
  const paginationQueryVariables = usePaginationQueryVariables();

  const { verifier } = useCurrentUserVerifier();

  const { data: permissiblePurposeDataList, loading } = usePermissiblePurposesListQuery({
    variables: {
      filter: {
        verifierId: {
          equals: verifier?.id,
        },
      },
      ...paginationQueryVariables,
      orderBy: { status: { sort: 'Desc', nulls: 'Last' } },
    },
  });

  const { crediblePermissiblePurposeOptions, isCrediblePermissiblePurposesLoading } =
    useCrediblePermissiblePurposeReferenceOptions(verifier);

  const isNewButtonDisabled =
    verifier?.credentialStatus !== VerifierCredentialStatus.Credentialed ||
    isCrediblePermissiblePurposesLoading ||
    crediblePermissiblePurposeOptions.length === 0;

  const extraTopNode = React.useMemo(() => {
    return (
      <Box width="100%">
        <Typography variant="subtitle1">Permissible Purposes</Typography>
        <Typography variant="body2" color="text.secondary">
          Your permissible purpose statuses are provided below. If you want to add additional
          permissible purposes to your account, click “New” to submit your request.
        </Typography>
      </Box>
    );
  }, []);

  const columns: Array<TableColumn<PermissiblePurposesItemFragment>> = [
    {
      title: 'Requested',
      key: 'createdAt',
      render: (_valueByDataPath, permissiblePurpose) => {
        return (
          <Typography variant="body2">
            {formatDate(permissiblePurpose.createdAt, DateFormatPatterns.shortDateWithSlash)}
          </Typography>
        );
      },
    },
    {
      title: 'Name',
      key: 'purposeValue',
      render: (_valueByDataPath, permissiblePurpose) => {
        return renderPurposeValue(permissiblePurpose);
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_valueByDataPath, permissiblePurpose) => {
        return (
          <Box display="flex" gap="5px" alignItems="center">
            <Typography variant="body2">{permissiblePurpose.status}</Typography>
            {permissiblePurpose.status === PermissiblePurposeStatus.Declined && (
              <Tooltip title={permissiblePurpose.denialReason}>
                <InfoIcon
                  color="action"
                  sx={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return isMobile ? (
    <Box padding={2}>
      <Box>
        {extraTopNode}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          fullWidth
          onClick={() => openModal(ModalId.PermissiblePurposeCreateModal)}
          size="medium"
          disabled={isNewButtonDisabled}
          sx={{ mt: 2 }}
        >
          New
        </Button>
      </Box>

      <Box mt={2}>
        {permissiblePurposeDataList?.permissiblePurposeList.map(permissiblePurpose => (
          <Paper sx={paperStyles} key={permissiblePurpose.id} elevation={2}>
            <Box>
              <Typography mb={1} variant="body1">
                Requested{' '}
                {formatDate(permissiblePurpose.createdAt, DateFormatPatterns.shortDateWithSlash)}
              </Typography>

              {renderPurposeValue(permissiblePurpose)}

              <Chip
                label={permissiblePurpose.status}
                size="small"
                variant="filled"
                sx={
                  permissiblePurpose.status === StatusLabel.Pending
                    ? chipStatusPendingStyles
                    : chipStatusActiveStyles
                }
              />
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  ) : (
    <React.Fragment>
      <Box display="flex" gap={2} alignItems="center">
        {extraTopNode}

        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModal(ModalId.PermissiblePurposeCreateModal)}
            size="medium"
            disabled={isNewButtonDisabled}
          >
            New
          </Button>
        </Box>
      </Box>

      <Table
        dataSource={permissiblePurposeDataList?.permissiblePurposeList ?? []}
        columns={columns}
        loading={loading}
        tableTheme={TableThemes.withBorder}
        hideFooter
      />
    </React.Fragment>
  );
};

const paperStyles: SxProp = {
  padding: '16px',
  marginBottom: '16px',
};

const chipStatusActiveStyles: SxProp = theme => ({
  backgroundColor: theme.palette.chip?.success?.main,
  color: theme.palette.chip?.success?.contrastText,
  borderColor: theme.palette.chip?.success?.contrastText,
  mt: 1,
});

const chipStatusPendingStyles: SxProp = theme => ({
  backgroundColor: theme.palette.chip?.warning?.main,
  color: theme.palette.chip?.warning?.contrastText,
  borderColor: theme.palette.chip?.warning?.contrastText,
  mt: 1,
});

const renderPurposeValue = (permissiblePurpose: PermissiblePurposesItemFragment) => (
  <Typography variant="body2" color="text.secondary">
    {permissiblePurpose.permissiblePurposeReference.label}
  </Typography>
);
